"use client";

import { askConfirm } from "@/lib/ui/confirmBus";
import { AUTH_REQUIRED, type ErrorCode } from "@/lib/api/errors";
import type { ActionName, ServerData } from "@/lib/api/actions";
import type { Loose } from "@/lib/api/json-types";
import { useEffect, useState } from "react";
import { PLATFORM_HOSTS, SURFACE_SEGMENTS } from "@/lib/network/domain";
import { noteLatency, noteNetworkFailure, noteNetworkOk } from "@/lib/ui/netHealth";
import { CHAT_PREFIX } from "@/lib/ui/aiChatStore";

export type { ActionName, ServerData };

// Клиентский помощник для мобильного приложения: зовёт /api, кладёт заголовок
// тенанта и Bearer-токен, возвращает конверт {ok,data}/{ok,error}.
// В dev тенант резолвится заголовком x-tenant; в проде — из сабдомена.

// Заведение берём из сабдомена — тот же механизм, что и в проде. На голом
// localhost сабдомена нет, поэтому падаем на demo; чтобы открыть локально
// другого клиента, заходить на {slug}.localhost:3000.
const FALLBACK_TENANT = "demo";

function cookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Сетевой домен (Э2): middleware ставит cookie `klait_network` на хосте папки,
 * и тогда ресторан — первый сегмент пути (`/hide/app`), а если путь без него
 * (адрес без префикса после редиректа) — запомненный `klait_restaurant`.
 * Путь, а не cookie, первичен: две вкладки с разными ресторанами не должны
 * слать запросы в один. Обычный хост → null.
 */
export function networkRestaurant(): string | null {
  if (typeof window === "undefined") return null;
  if (!cookieValue("klait_network")) return null;
  const seg = (window.location.pathname.split("/")[1] ?? "").toLowerCase();
  if (seg && !SURFACE_SEGMENTS.has(seg)) return seg;
  return cookieValue("klait_restaurant");
}

/** Префикс ресторана для ссылок внутри приложения: `/hide` на сетевом домене, иначе пусто. */
export function restaurantPrefix(): string {
  const r = networkRestaurant();
  return r ? `/${r}` : "";
}

/**
 * Префикс ресторана для ссылок — через состояние, а не при рендере: на
 * сервере префикса нет, и атрибут href разошёлся бы при гидратации.
 */
export function useRestaurantPrefix(): string {
  const [p, setP] = useState("");
  useEffect(() => {
    setP(restaurantPrefix());
  }, []);
  return p;
}

function tenantSlug(): string {
  if (typeof window === "undefined") return FALLBACK_TENANT;
  const net = networkRestaurant();
  if (net) return net;
  const labels = window.location.hostname.split(".");
  if (labels.length >= 2 && labels[0] && labels[0] !== "www") {
    // Общий вход (`app.klait.ru`, 12.10): ресторана у адреса нет, его
    // находит сервер по логину. Пустая строка — заголовок не шлём.
    if (PLATFORM_HOSTS.has(labels[0].toLowerCase())) return "";
    return labels[0].toLowerCase();
  }
  return FALLBACK_TENANT;
}

const TOKEN_KEY = "mise_token";
// Быстрый вход по PIN: помним логин/имя и факт установки PIN (сам PIN НЕ храним —
// только флаг; проверка PIN всегда на сервере через pinLogin). Лок — по неактивности.
const LOGIN_KEY = "mise_login";
const NAME_KEY = "mise_name";
const PIN_KEY = "mise_pin";
const ACTIVE_KEY = "mise_active";
// Префикс памяти чата помощника. Берётся ИЗ САМОГО хранилища: пока это была
// строка-копия, две половины одного знания жили в разных файлах и однажды
// разошлись бы (ревью 19.09). Хранилище переехало в lib/ui, поэтому импорт
// больше не тянет транспорт за экран десктопа.
// Выбранная локация сети. У ресторатора несколько заведений, и вопрос «какая
// точка» решается ЗДЕСЬ, на транспорте, а не в каждом экране: иначе один
// забытый запрос молча ушёл бы в другую точку — с чужими сменами и чужой
// зарплатой. Хранится рядом с токеном, потому что это часть контекста сессии.
//
// Значение — подсказка серверу, а не право: доступ к точке определяет
// membership, и заголовок с чужим venue просто не найдёт его (см.
// resolveMembership).
const VENUE_KEY = "mise_venue";
// Постоянный id этого браузера для чекина (30.10, «чекин без лазеек»): по нему
// сервер замечает отметку с чужого телефона. При выходе НЕ стирается — иначе
// друг, вышедший из своей учётки, получал бы «чистый» телефон.
const DEVICE_KEY = "klait_device_id";
const DEVICE_ACTIONS = new Set<string>(["checkIn", "checkOut"]);

function deviceId(): string | null {
  const s = ls();
  if (!s) return null;
  try {
    let id = s.getItem(DEVICE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      s.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}
// Запрашивать PIN, если пользователь не заходил дольше часа.
export const LOCK_MS = 60 * 60 * 1000;

// Действия, которые долго идут по делу: выгрузки, отправка документов,
// завершение инвентаризации (оно собирает xlsx и PDF и шлёт их в Telegram).
const HEAVY_ACTIONS = /^(export|send|finish|opImport|opSeed|getInventoryPrintBlank|iiko)/;

export type ApiResult<T = unknown> =
  | { ok: true; data: T }
  // `code` — машинный код отказа (12.10, `ERROR_CODES` в api/errors.ts).
  | { ok: false; error: string; code?: ErrorCode };

function ls(): Storage | null {
  return typeof window === "undefined" ? null : window.localStorage;
}

export function getToken(): string | null {
  return ls()?.getItem(TOKEN_KEY) ?? null;
}

export function setToken(token: string | null): void {
  const s = ls();
  if (!s) return;
  if (token) s.setItem(TOKEN_KEY, token);
  else s.removeItem(TOKEN_KEY);
}

export function getLogin(): string | null {
  return ls()?.getItem(LOGIN_KEY) ?? null;
}
export function getName(): string | null {
  return ls()?.getItem(NAME_KEY) ?? null;
}
// Запомнить, кто входил (для экрана PIN и pinLogin). Сам PIN не сохраняем.
export function rememberAuth(login: string, name: string): void {
  const s = ls();
  if (!s) return;
  s.setItem(LOGIN_KEY, login);
  s.setItem(NAME_KEY, name);
}
export function getPinSet(): boolean {
  return ls()?.getItem(PIN_KEY) === "1";
}
export function setPinSet(v: boolean): void {
  const s = ls();
  if (!s) return;
  if (v) s.setItem(PIN_KEY, "1");
  else s.removeItem(PIN_KEY);
}
// Отметить активность (последний раз «в приложении»).
export function touchActive(): void {
  ls()?.setItem(ACTIVE_KEY, String(Date.now()));
}
// Прошло больше часа с последней активности → требуется повторный PIN.
export function isLocked(): boolean {
  const s = ls();
  if (!s) return false;
  const t = Number(s.getItem(ACTIVE_KEY) || 0);
  return Date.now() - t > LOCK_MS;
}
// Выбранная локация. null = «сервер решит сам» (точка по умолчанию человека).
// На сетевом домене хранилище общее для всех ресторанов сети, а точка — у
// каждого своя: ключ дополняется рестораном, иначе в Sezon подставлялась бы
// точка Bistro и сервер отвечал «Нет доступа к этой точке» (Марат, 19.09).
function venueKey(): string {
  const r = networkRestaurant();
  return r ? `${VENUE_KEY}:${r}` : VENUE_KEY;
}
export function getVenue(): string | null {
  return ls()?.getItem(venueKey()) ?? null;
}
export function setVenue(venueId: string | null): void {
  const s = ls();
  if (!s) return;
  if (venueId) s.setItem(venueKey(), venueId);
  else s.removeItem(venueKey());
}

// Полный выход: чистим токен и всю память быстрого входа.
//
// Локацию тоже: следующий вход может быть под другим человеком, у которого этой
// точки нет вовсе, и он упёрся бы в «Нет доступа к этой точке» на пустом месте.
/**
 * Сессия ИСТЕКЛА (срок токена — 5 часов), но человек не выходил сам.
 *
 * Стираем только токен и отметку активности, а логин, имя и флаг «PIN
 * установлен» оставляем: оболочка увидит «токена нет, PIN есть» и попросит
 * PIN, а не пароль. Раньше здесь звался clearAuth() — он стирал и PIN, и
 * после истечения сессии телефон «вышибало на ввод логина и пароля, хотя
 * должен спрашивать только PIN» (правка Марата 21.09, [1]).
 *
 * Безопасность не меняется: pinLogin и так требует логин + PIN и считается
 * тем же счётчиком попыток, что и вход по паролю; хранить у себя логин
 * устройство и раньше могло. Полный выход — по-прежнему clearAuth().
 */
export function expireSession(): void {
  const s = ls();
  if (!s) return;
  s.removeItem(TOKEN_KEY);
  s.removeItem(ACTIVE_KEY);
}

export function clearAuth(): void {
  const s = ls();
  if (!s) return;
  for (const k of [TOKEN_KEY, LOGIN_KEY, NAME_KEY, PIN_KEY, ACTIVE_KEY]) s.removeItem(k);
  // Переписка с помощником переживает переход между разделами и перезагрузку
  // (src/app/_desk/aiChatStore.ts) — значит, при выходе её надо стереть явно:
  // за одним ноутбуком работают разные люди, а в ленте лежат имена и выручка.
  // Ключей несколько (ресторан × человек), поэтому чистим по префиксу.
  //
  // ⚠️ Выбранная точка чистится ТОЖЕ по префиксу. На сетевом домене ключ
  // имеет суффикс ресторана (mise_venue:hide), и голый VENUE_KEY его не
  // задевал: следующий человек за тем же ноутбуком стартовал с ЧУЖОЙ
  // выбранной точкой — это и есть штатный поставщик «устаревших подсказок»
  // в x-venue, из-за которых пришлось сужать исключение в rbac (аудит 19.09).
  const doomed: string[] = [];
  for (let i = 0; i < s.length; i++) {
    const k = s.key(i);
    if (k && (k.startsWith(CHAT_PREFIX) || k === VENUE_KEY || k.startsWith(VENUE_KEY + ":"))) doomed.push(k);
  }
  for (const k of doomed) s.removeItem(k);
}

// Имя действия, чей серверный ответ совместим с T. Компонент по-прежнему пишет
// `api<Liquidity>("getLiquidity")`, но теперь это ПРОВЕРКА, а не каст: если
// сервер может отдать `totals: null`, а T этого не знает, — ошибка компиляции.
// T = unknown (не задан) — подходит любое действие.
export type ActionNameFor<T> = {
  [K in ActionName]: ServerData<K> extends Loose<T> ? K : never;
}[ActionName];

/**
 * getSchedule отдаёт аватары одним словарём `avatars` (аудит 28.09, п. 4), а
 * брони — с `avatar: null`. Экраны читают `claim.avatar`, поэтому раскладываем
 * здесь, один раз для всех: у всех броней человека — ссылка на одну и ту же
 * строку, не копия. Ответ без словаря (старый сервер) не трогаем.
 */
export function hydrateScheduleAvatars(data: unknown): void {
  if (!data || typeof data !== "object") return;
  const d = data as { avatars?: unknown; shifts?: unknown };
  if (!d.avatars || typeof d.avatars !== "object" || !Array.isArray(d.shifts)) return;
  const avatars = d.avatars as Record<string, unknown>;
  for (const s of d.shifts) {
    const claims = s && typeof s === "object" ? (s as { claims?: unknown }).claims : undefined;
    if (!Array.isArray(claims)) continue;
    for (const c of claims) {
      if (!c || typeof c !== "object") continue;
      const claim = c as { userId?: unknown; avatar?: unknown };
      if (claim.avatar == null && typeof claim.userId === "string" && Object.hasOwn(avatars, claim.userId)) {
        const a = avatars[claim.userId];
        if (typeof a === "string") claim.avatar = a;
      }
    }
  }
}

// Действия входа и продления сами не продлеваются: иначе неудачный обмен
// ключа звал бы обмен ключа по кругу.
const NO_RETRY = new Set<string>(["login", "loginAny", "loginPick", "pinLogin", "refreshSession", "logout", "redeemHandoff"]);

let refreshing: Promise<boolean> | null = null;

/**
 * Новый пропуск по долгому ключу (12.10). Ключ лежит httpOnly-кукой ресторана
 * — скрипт его не видит, браузер сам отправит его в `/api`. Один обмен на все
 * запросы, упавшие разом: пропуск истёк для всех одновременно.
 *
 * Ответ «Вход обновляется — повторите» — соседняя вкладка обменяла тот же ключ
 * мгновением раньше; кука уже новая, повтор проходит.
 */
export function tryRefresh(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (refreshing) return refreshing;
  refreshing = (async () => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const r = await api<{ token: string }>("refreshSession");
      if (r.ok && r.data.token) {
        setToken(r.data.token);
        return true;
      }
      if (r.ok || r.code !== "refresh_race") return false;
      await new Promise((res) => setTimeout(res, 400));
    }
    return false;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/**
 * Выход по кнопке: гасим долгий ключ на сервере (иначе следующий открывший
 * вкладку вошёл бы им молча) и чистим память устройства. Ждём ответа сервера
 * недолго: нет связи — всё равно выходим, ключ истечёт сам.
 */
export async function signOut(): Promise<void> {
  await Promise.race([api("logout"), new Promise((res) => setTimeout(res, 1500))]);
  clearAuth();
}

/** Причина выхода «доступ закрыт» — до экрана входа, один раз (ревью 28.10, M1). */
const REVOKED_NOTICE_KEY = "klait_revoked_notice";

function noteRevoked(text: string): void {
  try {
    sessionStorage.setItem(REVOKED_NOTICE_KEY, text);
  } catch {
    // Хранилище недоступно — выход всё равно случится, без пояснения.
  }
}

/**
 * Забрать причину выхода, если сессию закрыли увольнением или отключением.
 * Экраны входа зовут при открытии: иначе человек после перезагрузки видел
 * пустую форму и не понимал, почему его выкинуло.
 */
export function takeRevokedNotice(): string | null {
  try {
    const v = sessionStorage.getItem(REVOKED_NOTICE_KEY);
    if (v) sessionStorage.removeItem(REVOKED_NOTICE_KEY);
    return v;
  } catch {
    return null;
  }
}

export async function api<T = unknown>(
  action: ActionNameFor<T>,
  payload?: unknown,
  retried = false,
): Promise<ApiResult<T>> {
  const slug = tenantSlug();
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(slug ? { "x-tenant": slug } : {}),
  };
  const token = getToken();
  if (token) headers["authorization"] = `Bearer ${token}`;
  // Локация — в КАЖДОМ запросе, а не в отдельных экранах. Сервер по нему
  // выбирает membership, то есть и права, и данные точки.
  const venue = getVenue();
  if (venue) headers["x-venue"] = venue;
  // Только на отметке: id устройства в каждом запросе серверу не нужен.
  if (DEVICE_ACTIONS.has(action)) {
    const dev = deviceId();
    if (dev) headers["x-klait-device-id"] = dev;
  }

  // Вокруг самого fetch, а не вокруг разбора JSON: симптом «похоже, VPN»
  // (netHealth.ts) — про то, что тупит СЕТЬ до сервера, а не наш парсинг
  // ответа. Замер снимается даже на ответах с отказом (4xx/5xx) — тормозной
  // 500 под VPN так же выдаёт деградацию, как и тормозной 200.
  const startedAt = Date.now();
  try {
    // Действие ДУБЛИРУЕТСЯ в адресе, хотя главным остаётся то, что в теле.
    //
    // Зачем: лимит на размер тела у загрузок файлов выше общего, а выбрать его
    // сервер обязан ДО чтения тела — иначе, чтобы узнать действие, он уже
    // прочитает то, что собирался отвергнуть. Из адреса действие видно сразу.
    const res = await fetch(`/api?action=${encodeURIComponent(action)}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ action, payload }),
    });
    // Тяжёлые действия в медиану не идут (ревью 20.09): выгрузка xlsx, PDF и
    // завершение инвентаризации законно занимают секунды, и человек без VPN
    // после трёх скачиваний получал плашку «похоже, включён VPN». Меряем
    // обычные запросы — по ним и видно, тормозит ли связь.
    if (!HEAVY_ACTIONS.test(action)) noteLatency(Date.now() - startedAt);
    // Разбор ответа — в СВОЁМ try (аудит 20.09, N4). Прокси или 502 отдают
    // HTML вместо JSON; раньше это падало в общий catch и засчитывалось как
    // обрыв связи — человеку показывали «похоже, VPN», хотя связь работает и
    // виноват сервер. Ответ доехал, значит связь есть.
    let json: ApiResult<T>;
    try {
      json = (await res.json()) as ApiResult<T>;
    } catch {
      noteNetworkOk();
      return { ok: false, error: "Сервер ответил непонятно — попробуйте ещё раз" };
    }
    // Сессия истекла посреди работы → на экран входа, а не «ошибка на кнопке».
    //
    // Срок сессии — 5 часов (jwt.ts, решение Марата 08.08), и раньше это
    // означало бы вот что: человек уходит на обед, возвращается, жмёт «Сохранить»
    // и получает красную строчку «Требуется авторизация». Дальше каждая кнопка
    // отвечает тем же, и пока не догадаешься перезагрузить страницу — экран
    // выглядит сломанным, а не «вас разлогинило».
    //
    // Условие `token` обязательно: без него экран входа зациклил бы сам себя —
    // там тоже ходят запросы, и они законно отвечают «Требуется авторизация».
    // Учётку уволили или отключили (17.10, Б17): ни продление, ни PIN не
    // помогут — выходим целиком, как по кнопке «Выйти». Без этого уволенный
    // видел «Доступ закрыт» на каждой кнопке, а с PIN — экран PIN по кругу.
    // Вход по паролю не трогаем: экран входа покажет текст сам.
    //
    // Перезагрузка — ТОЛЬКО если запрос шёл с пропуском (ревью 28.10, C1).
    // Продление при открытии страницы и вход по PIN идут без пропуска: там
    // перезагрузка снова звала бы продление, и страница крутилась бы по кругу,
    // не пуская к форме входа и следующего человека за тем же планшетом.
    // Причину выхода экран входа покажет сам (`takeRevokedNotice`).
    if (!json.ok && json.code === "auth_revoked" && (token || action === "pinLogin" || action === "refreshSession")) {
      clearAuth();
      noteRevoked(json.error);
      if (token && typeof window !== "undefined") window.location.reload();
      return json;
    }
    if (!json.ok && token && json.error === AUTH_REQUIRED && !NO_RETRY.has(action) && !retried) {
      // Пропуск истёк (5 часов) — молча берём новый по долгому ключу (12.10)
      // и повторяем запрос. Человек этого не видит: его не выкидывает посреди
      // смены. Не вышло (ключа нет, истёк, отозван) — как раньше, ниже.
      if (await tryRefresh()) return api<T>(action, payload, true);
    }
    if (!json.ok && token && json.error === AUTH_REQUIRED) {
      // Истечение сессии — не выход: если на устройстве стоит PIN, логин и PIN
      // остаются и оболочка спросит PIN (см. expireSession). Без PIN (десктоп,
      // общий ноутбук) — как раньше, полная очистка: следующий за этим
      // компьютером не должен увидеть чужой логин в поле входа.
      if (getPinSet()) expireSession();
      else clearAuth();
      // Перезагрузка, а не редирект: и десктоп, и приложение поднимают экран
      // входа сами, когда токена нет, — каждому своим маршрутом.
      if (typeof window !== "undefined") window.location.reload();
      return json;
    }
    // Активность продлевает окно без повторного PIN — но ТОЛЬКО для действий уже
    // вошедшего пользователя. login/pinLogin исключены: иначе неудачная попытка PIN
    // на экране блокировки обновила бы таймер и следующий reload проскочил бы PIN
    // по ещё живому токену (обход блокировки).
    // Смена пароля и «выйти везде, кроме этого» отзывают выданные пропуски —
    // этому устройству сервер выдал новый (28.10). Без него следующий запрос
    // ушёл бы со старым и потратил продление.
    if (json.ok && (action === "changePassword" || action === "revokeOtherDevices")) {
      const t = (json.data as { token?: unknown } | null)?.token;
      if (typeof t === "string" && t) setToken(t);
    }
    if (action !== "login" && action !== "pinLogin") touchActive();
    if (json.ok && action === "getSchedule") hydrateScheduleAvatars(json.data);
    // Человек уже на параллельной смене этой точки (бар + зал): спрашиваем
    // менеджера и, если да, повторяем назначение с подтверждением.
    if (
      !json.ok &&
      json.code === "parallel_shift" &&
      (await askConfirm(json.error))
    ) {
      return api<T>(action, { ...(payload as object), confirmParallel: true }, retried);
    }
    // Запрос доехал — неважно, с каким ответом. Отказ сервера это РАБОТАЮЩАЯ
    // связь, и путать его с обрывом нельзя (см. netHealth.ts).
    noteNetworkOk();
    return json;
  } catch {
    // Сюда попадает только настоящий сетевой сбой: fetch бросил, ответа нет.
    noteNetworkFailure();
    return { ok: false, error: "Нет связи с сервером" };
  }
}
